/**
 * The trucking `load_status` enum, and how each value is drawn.
 *
 * The seven values are the production enum, read from pg_enum on 2026-09-29:
 * booked, dispatched, en_route, arrived, delivered, cancelled, signed. `signed`
 * is absent from the API's schema.ts and real in the database — take the
 * database.
 *
 * WHY THIS FILE EXISTS. TruckingDispatch kept its own five-entry map (two of
 * them truck states no load can have) and looked a load up in it with no
 * fallback, so the first `cancelled` load on a tenant threw on `sc.dot` and
 * took the whole screen down. An unmapped value must never crash a page and
 * must never borrow a real status's look: it gets UNKNOWN_LOAD_STATUS, a slate
 * badge reading "—".
 *
 * Five signal hues for seven states, grouped by what the dispatcher does next,
 * with the label telling the members of a group apart:
 *
 *   slate  booked                         planned, nothing moving yet
 *   blue   dispatched, en_route, arrived  committed and in progress
 *   amber  delivered                      work done, waiting on a signature
 *   green  signed                         final — hours and quantities are
 *                                         fixed at signature, so this is the
 *                                         billable / payable state
 *   red    cancelled
 *
 * `delivered` and `signed` are deliberately different hues. A delivered load is
 * still owed a signature; drawing it green would tell the office it can bill.
 */
export const LOAD_STATUSES = [
  'booked',
  'dispatched',
  'en_route',
  'arrived',
  'delivered',
  'signed',
  'cancelled',
] as const;

export type LoadStatus = (typeof LOAD_STATUSES)[number];

export interface LoadStatusSkin {
  label: string;
  /** A --fi-signal-* fill. Dark ink goes on it; it is never an ink itself. */
  fill: string;
  /** The 4px left rail for a row or card carrying this status. */
  rail: string;
}

export const LOAD_STATUS_BADGE: Record<LoadStatus, LoadStatusSkin> = {
  booked: { label: 'Booked', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  dispatched: { label: 'Dispatched', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  en_route: { label: 'En Route', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  arrived: { label: 'Arrived', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  delivered: { label: 'Delivered', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  signed: { label: 'Signed', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

/** Keeps the rail's width and claims no colour: an unclassified row has no status. */
export const UNKNOWN_LOAD_STATUS: LoadStatusSkin = {
  label: '—',
  fill: 'bg-fi-signal-slate',
  rail: 'border-l-transparent',
};

export function isLoadStatus(value: unknown): value is LoadStatus {
  return typeof value === 'string' && (LOAD_STATUSES as readonly string[]).includes(value);
}

export function loadStatusSkin(status: string | null | undefined): LoadStatusSkin {
  return isLoadStatus(status) ? LOAD_STATUS_BADGE[status] : UNKNOWN_LOAD_STATUS;
}
